/**
 * HostA Global - Reset Password
 * Set a new password using the token from the email link.
 */

import { useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";

import Card from "@mui/material/Card";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";

import BasicLayout from "layouts/authentication/components/BasicLayout";
import { authAPI } from "utils/api";

import logo from "assets/images/logo-ct.png";

function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage({ type: "", text: "" });
    if (!token) {
      setMessage({ type: "error", text: "Invalid reset link. Please request a new password reset." });
      return;
    }
    if (newPassword.length < 6) {
      setMessage({ type: "error", text: "Password must be at least 6 characters long." });
      return;
    }
    if (newPassword !== confirmPassword) {
      setMessage({ type: "error", text: "Passwords do not match." });
      return;
    }
    setLoading(true);
    try {
      const res = await authAPI.resetPassword(token, newPassword);
      setMessage({ type: "success", text: res.message || "Password reset successfully." });
      setNewPassword("");
      setConfirmPassword("");
      setTimeout(() => navigate("/authentication/sign-in", { replace: true }), 2000);
    } catch (err) {
      setMessage({
        type: "error",
        text: err.message || "Invalid or expired link. Please request a new password reset.",
      });
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <BasicLayout>
        <Card sx={{ overflow: "visible", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
          <MDBox p={3}>
            <Alert severity="error">
              Invalid reset link. Please use the link from your email or{" "}
              <MDTypography component={Link} to="/authentication/forgot-password" fontWeight="medium" sx={{ color: "#b6925b" }}>
                request a new one
              </MDTypography>
              .
            </Alert>
            <MDBox mt={2} textAlign="center">
              <MDButton
                component={Link}
                to="/authentication/sign-in"
                variant="outlined"
                sx={{ borderColor: "#b6925b", color: "#b6925b" }}
              >
                Back to sign in
              </MDButton>
            </MDBox>
          </MDBox>
        </Card>
      </BasicLayout>
    );
  }

  return (
    <BasicLayout>
      <Card
        sx={{
          overflow: "visible",
          boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
        }}
      >
        <MDBox
          borderRadius="lg"
          mx={2}
          mt={-3}
          p={2}
          mb={1}
          textAlign="center"
          sx={{
            background: "#b6925b",
            boxShadow: "none",
          }}
        >
          <MDBox
            component="img"
            src={logo}
            alt="HostA Global"
            sx={{
              width: "100%",
              maxWidth: 220,
              height: "auto",
              objectFit: "contain",
              borderRadius: "5px",
              mt: 1,
              mb: 1,
              mixBlendMode: "multiply",
              filter: "contrast(1.05)",
            }}
          />
          <MDTypography variant="h6" fontWeight="regular" sx={{ color: "#fff" }} mt={2}>
            Set new password
          </MDTypography>
        </MDBox>
        <MDBox pt={4} pb={3} px={3}>
          <MDBox component="form" role="form" onSubmit={handleSubmit}>
            {message.text && (
              <MDBox mb={2}>
                <Alert severity={message.type}>{message.text}</Alert>
              </MDBox>
            )}
            <MDBox mb={2}>
              <MDInput
                type="password"
                label="New password"
                fullWidth
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                helperText="At least 6 characters"
              />
            </MDBox>
            <MDBox mb={2}>
              <MDInput
                type="password"
                label="Confirm new password"
                fullWidth
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
            </MDBox>
            <MDBox mt={4} mb={1}>
              <MDButton
                fullWidth
                type="submit"
                disabled={loading}
                sx={{
                  background: "#b99160",
                  color: "#fff",
                  "&:hover": { background: "#a67d4d" },
                  "&:disabled": { background: "#8a7358", color: "rgba(255,255,255,0.7)" },
                }}
              >
                {loading ? (
                  <>
                    <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                    Resetting...
                  </>
                ) : (
                  "Reset password"
                )}
              </MDButton>
            </MDBox>
            <MDBox mt={3} mb={1} textAlign="center">
              <MDTypography variant="button" color="text">
                <MDTypography
                  component={Link}
                  to="/authentication/sign-in"
                  variant="button"
                  fontWeight="medium"
                  sx={{ color: "#b6925b", "&:hover": { textDecoration: "underline" } }}
                >
                  Back to sign in
                </MDTypography>
              </MDTypography>
            </MDBox>
          </MDBox>
        </MDBox>
      </Card>
    </BasicLayout>
  );
}

export default ResetPassword;
